import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Chip,
  CircularProgress,
  FormGroup
} from "@mui/material";
import axios from "axios";

import { BACKEND_BASE_URL } from "../../config";
import { FormControlLabel } from "@material-ui/core";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

let debounceTimer;

export default function EditDataForm({ open, onClose, onSubmit, editData, api }) {
  console.log("EditDataForm render", editData);
  const [formData, setFormData] = useState({
    country: "",
    state: "",
    county: "",
    municipality_name: "",
    channel_url: "",
    website_url: "",
    channel_id: "",
    channel_type: "",
    notes: "", // Channel ID will be set here after fetching
    playlists: []
  });
  const [countryOptions, setCountryOptions] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  const [countyOptions, setCountyOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [muncipalityOptions, setMuncipalityOptions] = useState([]);
  // const [countyOptions, setCountyOptions] = useState([]);
  const [loadingMuncipality, setLoadingMuncipality] = useState(false);
  // const [loadingCounties, setLoadingCounties] = useState(false);
  const [playlistOptions, setPlaylistOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPlaylists, setIsLoadingPlaylists] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); // Tracks loading during initialization


  const fetchPlaylists = async (channel_id) => {
    setIsLoadingPlaylists(true); // Show loading spinner
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/fetch-playlists/`, {
        params: { channel_id, api_key: api }
      });
      setPlaylistOptions(response.data.playlists || []);
    } catch (error) {
      console.error("Error fetching playlists", error);

      let errorMessage =
        error.response?.data?.error || "Error fetching playlists";

      // Custom message for YouTube quota exceeded
      if (error.response?.status === 400) {
        errorMessage = error.response?.data?.error;
      }
      // Custom message for YouTube quota exceeded
      else if (error.response?.status === 429) {
        errorMessage = error.response?.data?.error;
      }
      else if (error.response?.status === 401) {
        errorMessage = error.response?.data?.error;
      }
      else if (error.response?.status === 403) {
        errorMessage = error.response?.data?.message;
      }
      else if (error.response?.status === 500) {
        errorMessage = error.response?.data?.message;
      }
      toast.error(`${errorMessage}`);
      // toast.error("");
    } finally {
      setIsLoadingPlaylists(false); // Hide loading spinner
    }
  };

  const fetchChannelIdAndPlaylists = async (url) => {
    try {
      const response = await axios.get(`${BACKEND_BASE_URL}/api/fetch-channel-id/`, {
        params: { url }
      });
      const channel_id = response.data.channel_id || "";
      setFormData((prevData) => ({
        ...prevData,
        channel_id
      }));
      if (channel_id) {
        await fetchPlaylists(channel_id);
      }
    } catch (error) {
      console.error("Error fetching channel ID", error);
      setFormData((prevData) => ({
        ...prevData,
        channel_id: ""
      }));
    }
  };

  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await axios.get(`${BACKEND_BASE_URL}/api/countries/`);
        if (Array.isArray(response.data)) {
          setCountryOptions(response.data); // store full objects
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setLoadingCountries(false);
      }
    };

    if (open) {
      fetchCountries();
    }
  }, [open]);

  useEffect(() => {
    const fetchStates = async () => {
      setLoadingStates(true);
      try {
        const response = await axios.get(`${BACKEND_BASE_URL}/api/states/`, {
          params: { country_id: formData.country } // sending country ID
        });
        if (response.data && response.data.states) {
          setStateOptions(response.data.states);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
      } finally {
        setLoadingStates(false);
      }
    };


    fetchStates();

  }, [formData.country]);

  useEffect(() => {
  if (stateOptions.length === 1) {
    setFormData((prev) => ({
      ...prev,
      state: stateOptions[0],
    }));
  }
}, [stateOptions]);

  useEffect(() => {
    const fetchCounties = async () => {
      if (formData.state) {
        setLoadingCounties(true);
        try {
          const response = await axios.get(`${BACKEND_BASE_URL}/api/counties/`, {
            params: { state: formData.state },
          });
          if (response.data && response.data.counties) {
            setCountyOptions(response.data.counties);
          }
        } catch (error) {
          console.error("Error fetching counties:", error);
        } finally {
          setLoadingCounties(false);
        }
      }
    };

    fetchCounties();
  }, [formData.state]);

  useEffect(() => {
    const fetchMunicipalities = async () => {
      if (formData.state && formData.county) { // Check for both state and county
        setLoadingMuncipality(true);
        try {
          const response = await axios.get(`${BACKEND_BASE_URL}/api/municipalities/filter/`, {
            params: {
              state: formData.state,
              county: formData.county, // Include the county
            },
          });

          if (response.data && response.data.municipalities) {
            setMuncipalityOptions(response.data.municipalities);
          } else {
            setMuncipalityOptions([]); // Handle empty or unexpected responses
          }
        } catch (error) {
          console.error("Error fetching municipalities:", error);
          setMuncipalityOptions([]); // Clear options on error
        } finally {
          setLoadingMuncipality(false);
        }
      }
    };

    fetchMunicipalities();
  }, [formData.state, formData.county]); // Correct dependency

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleChannelUrlChange = (e) => {
    const { value } = e?.target;
    setFormData((prevData) => ({
      ...prevData,
      channel_url: value
    }));

    clearTimeout(debounceTimer);

    debounceTimer = setTimeout(() => {
      if (value) {
        fetchChannelIdAndPlaylists(value);
      }
    }, 500);
  };

  const handlePlaylistsChange = (e) => {
    const { value } = e?.target;
    setFormData({ ...formData, playlists: value });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const playlists = formData.playlists.map((playlistId) => {

      if (playlistId === "whole-channel") {
        return {
          name: "Whole Channel",
          playlist_id: "whole-channel",
          playlist_url: formData.channel_url // or any URL you'd like for "Whole Channel"
        };
      }
      else if (playlistId === "live-channel") {
        return {
          name: "Live Channel",
          playlist_id: "live-channel",
          playlist_url: formData.channel_url // or any URL you'd like for "Whole Channel"
        };
      }
      const playlist = playlistOptions.find((option) => option.id === playlistId);
      if (playlist) {
        return {
          name: playlist.title,
          playlist_id: playlist.id,
          playlist_url: playlist.url
        };
      }
      // Handle missing playlist case (optional, depends on your requirements)
      console.warn(`Playlist with ID ${playlistId} not found in options.`);
      return null;
    }).filter(Boolean) // Remove any null entries

    const additional = {
      [formData.channel_type === "Board of Education" ? "bod" : "townCouncil"]: {
        channel_url: formData.channel_url,
        channel_id: "", // Provide the channel_id or other required fields
        notes: formData.notes,
        website_url: formData.website_url,
        playlists: playlists

      },
    };
    const dataToSend = {
      country: formData.country,
      state: formData.state,
      county: formData.county,
      municipality_name: formData.municipality_name,
      channel_url: formData.channel_url,
      website_url: formData.website_url,
      channel_type: formData.channel_type,
      playlists: playlists,
      additional: additional,


    };

    try {
      const response = await axios.put(`${BACKEND_BASE_URL}/api/update-url/${editData.id}/`, { ...dataToSend, api_key: api });
      onSubmit({ id: editData.id, ...response.data });
      onClose();
    } catch (error) {
      console.error("Error updating data:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (open && editData  && countryOptions.length > 0) {
      console.log(countryOptions, "countryOptions in EditDataForm")
      const updatedFormData = {
        country: countryOptions.find(c => c.name === editData.country)?.id || "", // Default to USA if not provided
        state: editData.state || "",
        county: editData.county || "",
        municipality_name: editData.municipality_name || "",
        channel_url: editData.channel_url || "",
        website_url: editData.website_url || "",
        channel_id: editData.channel_id || "",
        channel_type: editData.channel_type || "",
        playlists: editData.playlists?.map((playlist) => playlist.playlist_id) || [],
        notes: editData.notes

      };

      setFormData(updatedFormData);

      if (editData.channel_url) {
        setIsInitializing(true); // Start initialization
        fetchChannelIdAndPlaylists(editData.channel_url).finally(() =>
          setIsInitializing(false)
        ); // Stop initialization after fetch
      }
    }
  }, [open, editData, countryOptions]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Data</DialogTitle>
      <DialogContent style={{ maxHeight: "500px", overflowY: "auto" }}>

        {isInitializing ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
            <CircularProgress size={40} />
          </div>
        ) : (
          <>
            <FormControl fullWidth margin="dense">
              <InputLabel>Country</InputLabel>
              <Select
                name="country"
                value={formData.country}
                onChange={handleChange}
                fullWidth
              >
                {loadingCountries ? (
                  <MenuItem disabled>
                    <CircularProgress size={24} />
                    Loading Countries...
                  </MenuItem>
                ) : (
                  countryOptions.map((country) => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            {/* State Dropdown */}
            <FormControl fullWidth margin="dense">
              <InputLabel>State</InputLabel>
              <Select
                name="state"
                value={formData.state}
                onChange={handleChange}
                fullWidth
              >
                {loadingStates ? (
                  <MenuItem disabled>
                    <CircularProgress size={24} />
                    Loading States...
                  </MenuItem>
                ) : (
                  stateOptions.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* County Dropdown */}
            <FormControl fullWidth margin="dense" disabled={!formData.state}>
              <InputLabel>County</InputLabel>
              <Select
                name="county"
                value={formData.county}
                onChange={handleChange}
                fullWidth
              >
                {loadingCounties ? (
                  <MenuItem disabled>
                    <CircularProgress size={24} />
                    Loading Counties...
                  </MenuItem>
                ) : (
                  countyOptions.map((county) => (
                    <MenuItem key={county} value={county}>
                      {county}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            {/* Name Dropdown */}
            <FormControl fullWidth margin="dense" disabled={!formData.state}>
              <InputLabel>Town</InputLabel>
              <Select
                name="municipality_name"
                value={formData.municipality_name}
                onChange={handleChange}
                fullWidth
              >
                {loadingMuncipality ? (
                  <MenuItem disabled>
                    <CircularProgress size={24} />
                    Loading Towns...
                  </MenuItem>
                ) : muncipalityOptions.length === 0 ? (
                  <MenuItem disabled>No Town available</MenuItem>
                ) : (
                  muncipalityOptions.map((municipality) => (
                    <MenuItem key={municipality} value={municipality}>
                      {municipality}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>


            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.channel_type === "Board of Education"}
                    disabled // This disables the checkbox

                  />
                }
                label="Board of Education"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.channel_type === "Town Government"}
                    disabled // This disables the checkbox

                  />
                }
                label="Town Government"
              />
            </FormGroup>


            <TextField
              margin="dense"
              label={formData.channel_type === "Town Government" ? "Town Channel URL" : "Board of Education Channel URL"}
              name="channel_url"
              fullWidth
              value={formData.channel_url}
              onChange={handleChannelUrlChange}
            />

            <FormControl fullWidth margin="dense">
              <InputLabel>{formData.channel_type === "Town Government" ? "Town Playlist" : "Board of Education Playlist"}</InputLabel>
              <Select
                multiple

                name="playlists"
                value={formData.playlists}
                onChange={handlePlaylistsChange}
                fullWidth
                renderValue={(selected) => (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    {selected.map((playlistId) => {
                      if (playlistId === "whole-channel") {
                        // Render static "Whole Channel" chip
                        return <Chip key={playlistId} label="Main Channel" style={{
                          margin: "2px",
                          backgroundColor: "#152F8F",
                          color: "white",
                        }} />;
                      }
                      if (playlistId === "live-channel") {
                        // Render static "Whole Channel" chip
                        return <Chip key={playlistId} label="Live Channel" style={{
                          margin: "2px",
                          backgroundColor: "#152F8F",
                          color: "white",
                        }} />;
                      }
                      const playlist = playlistOptions.find(
                        (option) => option.id === playlistId
                      );
                      return playlist ? (
                        <Chip key={playlistId} label={playlist.title} style={{
                          margin: "2px",
                          backgroundColor: "#152F8F",
                          color: "white",
                        }} />
                      ) : null;
                    })}
                  </div>
                )}
              >

                {/* Add static "Whole Channel" option */}
                <MenuItem value="whole-channel">
                  <Checkbox checked={formData.playlists.includes('whole-channel')} />
                  <ListItemText primary="Main Channel" />
                </MenuItem>
                <MenuItem value="live-channel">
                  <Checkbox checked={formData.playlists.includes('live-channel')} />
                  <ListItemText primary="Live Channel" />
                </MenuItem>
                {isLoadingPlaylists ? (
                  <MenuItem disabled>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <CircularProgress size={24} />
                      Loading Playlists...
                    </div>
                  </MenuItem>
                ) : playlistOptions.length === 0 ? (
                  <MenuItem disabled>No playlists available</MenuItem>
                ) : (
                  playlistOptions.map((playlist) => (
                    <MenuItem key={playlist.id} value={playlist.id}>
                      <Checkbox
                        checked={formData.playlists.includes(playlist.id)}
                      />
                      <ListItemText primary={playlist.title} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <TextField
              margin="dense"
              label="Notes"
              name="notes"
              fullWidth
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e?.target?.value })
              }
            />

            <TextField
              margin="dense"
              label="Webiste URL"
              name="website_url"
              fullWidth
              value={formData.website_url}
              onChange={(e) =>
                setFormData({ ...formData, website_url: e?.target?.value })
              }
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          style={{ backgroundColor: "red", color: "white" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          style={{ backgroundColor: "#0B194A", color: "white" }}
          disabled={isSubmitting}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
